import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useVerbandbuch from '../../hooks/useVerbandbuch';

const CREATE_RIGHT = 'verbandbuch:create';

const nowLocal = () => dayjs().format('YYYY-MM-DDTHH:mm');

const Verbandbuch = () => {
  const authCtx = useContext(AuthContext);
  const hasRight = authCtx.checkRight(CREATE_RIGHT);
  const { getBranches, createEntry } = useVerbandbuch();

  const injuredNameRef = useRef(null);
  const injuredContactRef = useRef(null);
  const workAreaRef = useRef(null);
  const accidentAtRef = useRef(null);
  const accidentLocationRef = useRef(null);
  const accidentDescriptionRef = useRef(null);
  const injuryDescriptionRef = useRef(null);
  const witnessesRef = useRef(null);
  const firstAidAtRef = useRef(null);
  const firstAidMeasuresRef = useRef(null);
  const firstAiderNameRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!hasRight) return;
    const loadBranches = async () => {
      try {
        const result = await getBranches();
        setBranches(result?.branches || []);
      } catch (error) {
        toast.error('Fehler beim Laden der Standorte.');
      }
    };
    loadBranches();
  }, [hasRight, getBranches]);

  const resetForm = useCallback(() => {
    [
      injuredNameRef,
      injuredContactRef,
      workAreaRef,
      accidentLocationRef,
      accidentDescriptionRef,
      injuryDescriptionRef,
      witnessesRef,
      firstAidAtRef,
      firstAidMeasuresRef,
      firstAiderNameRef,
    ].forEach(ref => {
      if (ref.current) ref.current.value = '';
    });
    if (accidentAtRef.current) accidentAtRef.current.value = nowLocal();
  }, []);

  const handleSubmit = useCallback(async () => {
    const injuredName = injuredNameRef.current?.value?.trim() || '';
    const injuredContact = injuredContactRef.current?.value?.trim() || '';
    const workArea = workAreaRef.current?.value?.trim() || '';
    const accidentAtValue = accidentAtRef.current?.value || '';
    const accidentDescription = accidentDescriptionRef.current?.value?.trim() || '';
    const injuryDescription = injuryDescriptionRef.current?.value?.trim() || '';

    if (!injuredName || !injuredContact || !workArea) {
      toast.error('Bitte alle Angaben zur Person ausfüllen.');
      return;
    }
    if (!branchId) {
      toast.error('Bitte einen Standort auswählen.');
      return;
    }
    if (!accidentAtValue || !dayjs(accidentAtValue).isValid()) {
      toast.error('Bitte Datum und Uhrzeit des Unfalls angeben.');
      return;
    }
    if (!accidentDescription || !injuryDescription) {
      toast.error('Bitte Hergang sowie Art und Umfang der Verletzung angeben.');
      return;
    }

    const firstAidAtValue = firstAidAtRef.current?.value || '';
    if (firstAidAtValue && !dayjs(firstAidAtValue).isValid()) {
      toast.error('Das Datum der Erste-Hilfe-Leistung ist ungültig.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createEntry({
        branchId,
        injuredName,
        injuredContact,
        workArea,
        accidentAt: dayjs(accidentAtValue).toISOString(),
        accidentLocation: accidentLocationRef.current?.value?.trim() || '',
        accidentDescription,
        injuryDescription,
        witnesses: witnessesRef.current?.value?.trim() || '',
        firstAidAt: firstAidAtValue ? dayjs(firstAidAtValue).toISOString() : '',
        firstAidMeasures: firstAidMeasuresRef.current?.value?.trim() || '',
        firstAiderName: firstAiderNameRef.current?.value?.trim() || '',
      });
      toast.success('Verbandbuch-Eintrag gespeichert.');
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Speichern des Verbandbuch-Eintrags.');
    } finally {
      setIsSubmitting(false);
    }
  }, [branchId, createEntry, resetForm]);

  const submitSafely = useCallback(() => {
    handleSubmit().catch(() => {});
  }, [handleSubmit]);

  if (!hasRight) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography
        variant='h5'
        component='h1'
        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <HealthAndSafetyIcon color='error' /> Verbandbuch
      </Typography>
      <Alert severity='info' sx={{ mb: 3 }}>
        Jede Erste-Hilfe-Leistung muss dokumentiert werden. Die Angaben werden vertraulich
        behandelt und sind nur für berechtigte Personen einsehbar.
      </Alert>

      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
          Angaben zur Person
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Name des Verletzten / Erkrankten'
              inputRef={injuredNameRef}
              defaultValue=''
              required
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Erreichbar unter Telefonnummer / E-Mail'
              inputRef={injuredContactRef}
              defaultValue=''
              required
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Arbeitsbereich / Tätigkeit'
              inputRef={workAreaRef}
              defaultValue=''
              required
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size='small' fullWidth required>
              <InputLabel id='verbandbuch-branch-label'>Standort</InputLabel>
              <Select
                labelId='verbandbuch-branch-label'
                label='Standort'
                value={branchId}
                onChange={event => setBranchId(event.target.value)}
              >
                {branches.map(branch => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
          Angaben zum Unfall / zur Erkrankung
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Datum und Uhrzeit des Unfalls'
              type='datetime-local'
              inputRef={accidentAtRef}
              defaultValue={nowLocal()}
              required
              fullWidth
              size='small'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Unfallort (z. B. Arena, Küche, Theke)'
              inputRef={accidentLocationRef}
              defaultValue=''
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Hergang des Unfalls'
              inputRef={accidentDescriptionRef}
              defaultValue=''
              required
              fullWidth
              multiline
              minRows={3}
              size='small'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Art und Umfang der Verletzung / Erkrankung'
              inputRef={injuryDescriptionRef}
              defaultValue=''
              required
              fullWidth
              multiline
              minRows={2}
              size='small'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Name der Zeugen (optional)'
              inputRef={witnessesRef}
              defaultValue=''
              fullWidth
              size='small'
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
          Erste-Hilfe-Leistung
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Datum und Uhrzeit der Erste-Hilfe-Leistung'
              type='datetime-local'
              inputRef={firstAidAtRef}
              defaultValue=''
              fullWidth
              size='small'
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label='Name des Ersthelfers / der Ersthelferin'
              inputRef={firstAiderNameRef}
              defaultValue=''
              fullWidth
              size='small'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Art und Weise der Erste-Hilfe-Maßnahmen'
              inputRef={firstAidMeasuresRef}
              defaultValue=''
              fullWidth
              multiline
              minRows={2}
              size='small'
            />
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant='contained'
        size='large'
        onClick={submitSafely}
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={18} color='inherit' /> : null}
      >
        Eintrag speichern
      </Button>
    </Box>
  );
};

export default Verbandbuch;
